import {
  Benefit,
  ClaimAmount,
  ClaimPropertiesResponse,
  ClaimStatuses,
  ClaimType,
  ViewClaimDetailsResponse,
} from 'all-in-one-shared-api';

import { getLocaleDescription as getDescription } from './getLocaleDescription';

type Receipt = Readonly<ClaimAmount> & {
  description?: string;
};

export type FormattedClaimDetails = ViewClaimDetailsResponse & {
  claimType: ClaimType;
  claimStatus: ClaimStatuses;
  receipts: Receipt[];
  claimTypeDescription?: string | undefined;
  doctorName: string;
  diagnosisName: string;
  treatmentDate: string;
  receiptTitle?: string;
  attachmentTitle?: string;
};

export const formatClaimDetails = (
  {
    benefits,
    claimType,
    diagnosis,
    doctor,
    receiptAmounts,
    statusHistories,
    ...data
  }: Readonly<ViewClaimDetailsResponse>,
  claimProperties: ClaimPropertiesResponse,
  isEnglish: boolean,
): FormattedClaimDetails & Readonly<ViewClaimDetailsResponse> => {
  let receipts: (Receipt | ClaimAmount)[] = [];
  let claimTypeDescription: string | undefined;
  let receiptTitle = 'receipts';
  let attachmentTitle = 'referralLetter';
  const outpatientBenefits = claimProperties?.claimTypes?.find(({ code }) => code === 'OP')?.benefits;
  const defaultClaimTypeDescription = isEnglish ? claimType.description : claimType.chineseDescription;

  const benefitCodes = benefits.map(({ code }) => code);
  if (benefits.length == 0) {
    receipts = receiptAmounts || [];
    claimTypeDescription = defaultClaimTypeDescription;
    receiptTitle = (claimProperties?.claimTypes || [])
      .filter(includeCodeAndHasAttachmentIndex([claimType.code]))
      .flatMap(getLocaleReceiptTitles(isEnglish))[0];
    attachmentTitle = (claimProperties?.claimTypes || [])
      .filter(includeCodeAndHasAttachmentIndex([claimType.code], 1))
      .flatMap(getLocaleAttachmentTitles(isEnglish))[0];
  } else if (benefits.length == 1) {
    receipts = receiptAmounts || [];
    claimTypeDescription = benefits.map(getLocaleDescription(isEnglish)).join(', ')
      ? benefits.map(getLocaleDescription(isEnglish)).join(', ')
      : defaultClaimTypeDescription;
    receiptTitle = [
      ...new Set(
        (outpatientBenefits || [])
          .filter(includeCodeAndHasAttachmentIndex(benefitCodes))
          .flatMap(getLocaleReceiptTitles(isEnglish)),
      ),
    ].join(', ');
    attachmentTitle = [
      ...new Set(
        (outpatientBenefits || [])
          .filter(includeCodeAndHasAttachmentIndex(benefitCodes, 1))
          .flatMap(getLocaleAttachmentTitles(isEnglish)),
      ),
    ].join(', ');
  } else {
    receipts = (receiptAmounts || []).map(includeBenefitDescription(isEnglish, benefits));
    claimTypeDescription = benefits.map(getLocaleDescription(isEnglish)).join(', ');
    receiptTitle = [
      ...new Set(
        (outpatientBenefits || [])
          .filter(includeCodeAndHasAttachmentIndex(benefitCodes))
          .flatMap(getLocaleReceiptTitles(isEnglish)),
      ),
    ].join(', ');
    attachmentTitle = [
      ...new Set(
        ((claimProperties?.claimTypes?.find(({ code }) => code === 'OP') || {}).benefits || [])
          .filter(includeCodeAndHasAttachmentIndex(benefitCodes, 1))
          .flatMap(getLocaleAttachmentTitles(isEnglish)),
      ),
    ].join(', ');
  }

  return {
    ...data,
    claimType,
    benefits,
    diagnosis,
    doctor,
    receiptAmounts,
    receipts,
    claimTypeDescription,
    doctorName: getDescription(isEnglish)(doctor),
    diagnosisName: getDescription(isEnglish)(diagnosis),
    receiptTitle,
    attachmentTitle,
    statusHistories: [...statusHistories].reverse(),
  };
};

function includeCodeAndHasAttachmentIndex(
  typeCodes: (string | undefined)[],
  length = 0,
): (value: ClaimType, index: number, array: ClaimType[]) => unknown {
  return ({ code, requiredAttachments }) => typeCodes.includes(code) && (requiredAttachments || []).length > length;
}

function getLocaleReceiptTitles(
  isEnglish: boolean,
): (this: undefined, value: ClaimType, index: number, array: ClaimType[]) => string | readonly string[] {
  return ({ requiredAttachments, chineseRequiredAttachments }) =>
    isEnglish ? (requiredAttachments || [])[0] : (chineseRequiredAttachments || [])[0];
}

function getLocaleAttachmentTitles(
  isEnglish: boolean,
): (this: undefined, value: ClaimType, index: number, array: ClaimType[]) => string | readonly string[] {
  return ({ requiredAttachments, chineseRequiredAttachments }) =>
    isEnglish
      ? (requiredAttachments || []).slice(1, (requiredAttachments || []).length)
      : (chineseRequiredAttachments || []).slice(1, (chineseRequiredAttachments || []).length);
}

export function getLocaleDescription(
  isEnglish: boolean,
): (value: Benefit, index: number, array: Benefit[]) => string | undefined {
  return ({ description, chineseDescription }) => (isEnglish ? description : chineseDescription);
}

function includeBenefitDescription(
  isEnglish: boolean,
  benefits: Benefit[],
): (
  value: ClaimAmount,
  index: number,
  array: ClaimAmount[],
) => { description: string | undefined; amount: string; currency: string } {
  return (amount, i) => {
    if (i >= benefits.length) {
      return {
        ...amount,
        description: 'Error data when development',
      };
    }
    return {
      ...amount,
      description: isEnglish ? benefits[i].description : benefits[i].chineseDescription,
    };
  };
}
